import React, { ReactNode } from 'react';

import { TourStepProps } from 'antd';
import { DATA_IDS } from '@phonepe/galactus';

import { landingBanner } from '@/assets/images';

export const COACH_MARK_ATTRIBUTE = 'data-id';

export const CoachMarkTargets = {
	TRANSACTIONS: 'transactions',
	DASHBOARD: 'dashboard',
	SETTLEMENTS: 'settlements',
	REPORTS: 'reports',
	NOTIFICATIONS: 'notifications',
	DASHBOARD_SETTLEMENTS: 'dashboardSettlements',
	LIVE_TRANSACTIONS: 'liveTransactions',
	TRANSACTIONS_HISTORY: 'transactionsHistory',
	TRANSACTIONS_HISTORY_NCMC: 'transactionsHistoryNcmc',
	SEARCH: DATA_IDS.COACHMARK_SEARCH,
	CUSTOM_VIEW: DATA_IDS.COACHMARK_CUSTOM_VIEW,
	FILTERS: DATA_IDS.COACHMARK_FILTERS,
	INITIATE_BULK_REFUND: DATA_IDS.INITIATE_BULK_REFUND_BUTTON,
	PROFILE: 'profile',
	PAYMENT_LINKS: 'paymentLinks',
	CREATE_PAYMENT_LINKS: 'createPaymentLinks',
	REFUNDS: 'refunds',
	ALL_REFUNDS: 'allRefunds',
	ALL_REFUNDS_NCMC: 'allRefundsNcmc',
	BULK_REFUNDS: 'bulkBatchRefunds',
	REPORTS_LINK: 'reportsLink',
} as const;
export interface ICoachMarkStepCore<K = string> {
	title: ReactNode;
	description?: ReactNode;
	cover?: ReactNode;
	id: K;
	placement?: TourStepProps['placement'];
}

export const CoachMarks = {
	sidebar: [
		{
			title: 'Explore Your Profile',
			description:
				'Welcome to your profile! This is your personal space where you can manage your account settings, update your information. Easily switch between login profiles from this convenient space, allowing seamless transitions between different roles/accounts for a personalized journey.',
			id: CoachMarkTargets.PROFILE,
			placement: 'rightTop',
		},
		{
			title: 'Discover Home Page Insights',
			description:
				"Explore trends on the Home Page Insights. Leverage these valuable data points to enhance your strategies and faster growth. Let's make informed decisions!",
			id: CoachMarkTargets.DASHBOARD,
			placement: 'rightBottom',
		},
		{
			title: 'Explore your transactions',
			description: `Access your financial records in the 'Transactions' tab. Use filters, quick actions, and insights for efficient tracking. Export data and get real-time updates.`,
			id: CoachMarkTargets.TRANSACTIONS,
			placement: 'right',
		},
		{
			title: 'Dive into reports',
			description: `In the 'Reports' tab, access daily transaction reports and generate custom reports. Tailor your insights for better decision-making.`,
			id: CoachMarkTargets.REPORTS,
			placement: 'right',
		},
		{
			title: 'Click here to start creating Payment Links',
			description:
				'Accept payments from customers directly by sending them Payment Links',
			id: CoachMarkTargets.PAYMENT_LINKS,
			placement: 'right',
		},
		{
			title: 'Track Your Settlements with Ease',
			description:
				'Keep track of your settlements effortlessly in the "Settlement" section. Stay informed about your payment status and manage your finances smoothly.',
			id: CoachMarkTargets.SETTLEMENTS,
			placement: 'right',
		},
	],
	dashboard: [
		// {
		// 	title: 'Stay Informed with Notifications',
		// 	description: `Stay updated with Real-time alerts for reports,transactions, settlements, and account activities. Check the bell icon for updates.`,
		// 	id: CoachMarkTargets.NOTIFICATIONS,
		// 	placement: 'bottom',
		// },
		// {
		// 	title: 'Track Your Settlements with Ease',
		// 	description: `Keep track of your settlements effortlessly in the "Settlement Updates" section. Stay informed about your payment status and manage your finances smoothly.`,
		// 	id: CoachMarkTargets.DASHBOARD_SETTLEMENTS,
		// 	placement: 'top',
		// },
	],
	paymentLinks: [
		{
			title: 'Create New Payment Link',
			description: 'Click here to create a new payment link.',
			id: CoachMarkTargets.CREATE_PAYMENT_LINKS,
			placement: 'bottom',
		},
		{
			title: 'Payment Link Form',
			description:
				'Enter details like amount, reference ID, customer details, etc to create a new payment link.',
			cover: (
				<img
					style={{ height: '300px', objectFit: 'contain' }}
					alt="Payment Link Form"
					src="https://imgstatic.phonepe.com/images/pfb-web/common/paylinks/3456/1982/creation-modal.png"
				/>
			),
			id: 'payment-link-form',
			placement: 'center',
		},
		{
			title: 'Payment Link List',
			description:
				'All payment links you create will appear in a list on the dashboard. You can copy the link directly from here.',
			cover: (
				<img
					style={{ height: '300px', objectFit: 'contain' }}
					alt="Payment Link List"
					src="https://imgstatic.phonepe.com/images/pfb-web/common/paylinks/2958/1720/list-view.png"
				/>
			),
			id: 'payment-link-list',
			placement: 'center',
		},
		{
			title: 'Payment Link Details',
			description:
				'Click on any payment link to view details or take action such as sharing or cancelling the link.',
			cover: (
				<img
					style={{ height: '300px', objectFit: 'contain' }}
					alt="Payment Link Details"
					src="https://imgstatic.phonepe.com/images/pfb-web/common/paylinks/2900/1984/details-view.png"
				/>
			),
			id: 'payment-link-details',
			placement: 'center',
		},
	] as ICoachMarkStepCore[],
	transactions: [
		{
			title: 'Real-Time Transaction Tracking',
			description: `Live Transactions tab offers real-time insights into ongoing activities. Monitor transactions as they happen, stay updated, and make timely decisions with confidence.`,
			id: CoachMarkTargets.LIVE_TRANSACTIONS,
			placement: 'bottom',
		},
		{
			title: 'Complete Transaction History',
			description: `Access your full transaction history for a comprehensive view of past activities.`,
			id: CoachMarkTargets.TRANSACTIONS_HISTORY,
			placement: 'bottom',
		},
		{
			title: 'View and Manage Refunds',
			description: `Explore all your refunds conveniently in one place !`,
			id: CoachMarkTargets.REFUNDS,
			placement: 'bottom',
		},
	],
	transactionsHistory: [
		{
			title: 'Refine Your View with Filters',
			description: `Tailor your view by using advanced filters like Stores/Terminals/QR codes for any date range and get the data you need.`,
			id: CoachMarkTargets.FILTERS,
			placement: 'bottom',
		},
		{
			title: 'Effortless Transaction Search',
			description: `Streamline your search process by querying transactions with phone numbers, UTR numbers, transaction IDs, and more. Find what you need swiftly, improving your efficiency and productivity`,
			id: CoachMarkTargets.SEARCH,
			placement: 'bottom',
		},
		{
			title: 'Tailored Custom View',
			description: `Personalize your view with Custom Columns. Display the data that matters most to you, optimizing your workflow. Don't forget to use "Print Summary" for a comprehensive overview.`,
			id: CoachMarkTargets.CUSTOM_VIEW,
			placement: 'left',
		},
	],
	transactionsHistoryNcmc: [
		{
			title: 'Refine Your View with Filters',
			description: `Tailor your view by using advanced filters like Payment Mode, Payment Solution, Stores, Terminals and more for any date range and get the data you need.`,
			id: CoachMarkTargets.FILTERS,
			placement: 'bottom',
		},
		{
			title: 'Effortless Transaction Search',
			description: `Streamline your search process by querying transactions with UTR numbers, transaction IDs, store IDs and more. Find what you need swiftly, improving your efficiency and productivity`,
			id: CoachMarkTargets.SEARCH,
			placement: 'bottom',
		},
		{
			title: 'Tailored Custom View',
			description: `Personalize your view with Custom Columns. Display the data that matters most to you, optimizing your workflow. Don't forget to use "Print Summary" for a comprehensive overview.`,
			id: CoachMarkTargets.CUSTOM_VIEW,
			placement: 'left',
		},
	],
	allRefunds: [
		{
			title: 'View and Manage Refunds',
			description: `Explore all your refunds conveniently in one place !`,
			id: CoachMarkTargets.REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Access all historic refunds',
			description: `Navigate to the 'All Refunds' tab to view a
			comprenensive list of all requested
			refunds.`,
			id: CoachMarkTargets.ALL_REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Access all Bulk refunds',
			description: `Easily manage bulk refunds uploaded by you.`,
			id: CoachMarkTargets.BULK_REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Refine Your View with Filters',
			description: `Tailor your view by using filters like Refund Status, Mode of Payment for any date range and get the data you need.`,
			id: CoachMarkTargets.FILTERS,
			placement: 'bottom',
		},
		{
			title: 'Effortless Refund Search',
			description: `Streamline your search process by querying refunds with phone numbers, refund IDs and transaction IDs. Find what you need swiftly, improving your efficiency and productivity`,
			id: CoachMarkTargets.SEARCH,
			placement: 'bottom',
		},
		{
			title: 'Refund Report for detailed insight',
			description: `Navigate to the 'Refund Report' tab to access comprehensive reports and download them for your records.`,
			id: CoachMarkTargets.REPORTS_LINK,
			placement: 'left',
		},
	],
	allRefundsNcmc: [
		{
			title: 'View and Manage Refunds',
			description: `Explore all your refunds conveniently in one place !`,
			id: CoachMarkTargets.REFUNDS,
			placement: 'bottom',
		},

		{
			title: 'Refine Your View with Filters',
			description: `Tailor your view by using filters like Refund Status, Payment Mode for any date range and get the data you need.`,
			id: CoachMarkTargets.FILTERS,
			placement: 'bottom',
		},
		{
			title: 'Effortless Refund Search',
			description: `Streamline your search process by querying refunds with refund IDs and transaction IDs. Find what you need swiftly, improving your efficiency and productivity`,
			id: CoachMarkTargets.SEARCH,
			placement: 'bottom',
		},
	],
	bulkBatchRefunds: [
		{
			title: 'View and Manage Refunds',
			description: `Explore all your refunds conveniently in one place !`,
			id: CoachMarkTargets.REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Access all historic refunds',
			description: `Navigate to the 'All Refunds' tab to view a
			comprenensive list of all requested
			refunds.`,
			id: CoachMarkTargets.ALL_REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Access all Bulk refunds',
			description: `Easily manage bulk refunds uploaded by you.`,
			id: CoachMarkTargets.BULK_REFUNDS,
			placement: 'bottom',
		},
		{
			title: 'Refine Your View with Filters',
			description: `Tailor your view by using Refund status filter for any date range and get the data you need.`,
			id: CoachMarkTargets.FILTERS,
			placement: 'bottom',
		},
		{
			title: 'Process Multiple Refunds At Once',
			description: `Simplify large-scale refunds with ease! Use the 'Initiate Bulk Refund' button to process multiple refunds efficiently.`,
			id: CoachMarkTargets.INITIATE_BULK_REFUND,
			placement: 'left',
		},
		{
			title: 'Refund Report for detailed insight',
			description: `Navigate to the 'Refund Report' tab to access comprehensive reports and download them for your records.`,
			id: CoachMarkTargets.REPORTS_LINK,
			placement: 'left',
		},
	],
} as const;

export const TourModals = {
	landing: {
		banner: landingBanner,
		title: 'Welcome to the PhonePe Business Web Dashboard!',
		description: `Easily manage your business with real-time notifications, efficient financial record-keeping, and detailed transaction reports. Streamline your business oversight with transparent settlement details, centralized store, and staff management, and quick access to dedicated support. Welcome to a more streamlined way to oversee your business with PhonePe Business Web!`,
	},
} as const;

export type CoachMarkGroupId = keyof typeof CoachMarks;
export type TourModalId = keyof typeof TourModals;

export interface ICoachMarkStep<K = string> extends ICoachMarkStepCore<K> {
	groupId: CoachMarkGroupId;
}
